import { DefaultContainedButton, DefaultOutlinedButton } from '@/components/buttons';
import { CustomerActions, CustomerSelectors } from '@/store/customer';
import { FormsActions, FormsSelector } from '@/store/forms';
import { SnackbarActions } from '@/store/snackbar';
import { Box, MenuItem, Select, Stack, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BandwidthField, FundingStrategyField } from './FormFields';
export const OrganizationForm = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const formId = 'userForm';
    const loading = useSelector(CustomerSelectors.getLoading);
    const customerError = useSelector(CustomerSelectors.getError);
    const formValues = useSelector((state) => FormsSelector.getFormValues(state, formId));
    useEffect(() => {
        if (customerError) {
            dispatch(SnackbarActions.enqueueSnackbar({ message: customerError }));
            dispatch(CustomerActions.resetError());
        }
    }, [customerError]);
    const { email = '', firstName = '', lastName = '', grantExperience = '', organizationName = '', organizationType = '', organizationDescription = '', fundingStrategy = 'noFormalPlan', bandwidth = 'noBandwidth', } = formValues;
    const handleSubmit = async (e) => {
        e.preventDefault();
        const body = {
            email,
            firstName,
            lastName,
            grantExperience,
            company: organizationName,
            companyType: organizationType,
            fundingStrategy: fundingStrategy,
            bandwidth: bandwidth,
            companyDescription: organizationDescription,
        };
        const result = await dispatch(CustomerActions.createCustomerAsync({ body, options: { params: { upsert: true } } }));
        if (CustomerActions.createCustomerAsync.fulfilled.match(result)) {
            dispatch(FormsActions.setValue({ id: 'steps', step: 2 }));
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        handleFormValues(name, value);
    };
    const handleFormValues = (name, value) => {
        dispatch(FormsActions.setValue({ id: formId, [name]: value }));
    };
    return (React.createElement(Box, { my: 4, px: 2, width: "100%", maxWidth: "880px" },
        React.createElement(Typography, { mb: 1, color: "secondary", fontSize: "1rem", fontWeight: 600 }, "About your organization"),
        React.createElement(Typography, { color: "secondary.light", fontSize: "0.875rem", fontWeight: 500 }, "Give us a brief overview of the project. Feel free to paste in previous abstracts, organization descriptions, or any other content you have."),
        React.createElement("form", { onSubmit: handleSubmit, autoComplete: "off" },
            React.createElement(Stack, { p: { sm: 4 }, my: 3, gap: 5, sx: { border: { sm: `1px solid ${theme.palette.grey[200]}` }, borderRadius: '8px' } },
                React.createElement(Box, null,
                    React.createElement(Box, { mb: 3 },
                        React.createElement(Typography, { mb: 0.5, fontSize: "14px", fontWeight: 500, color: "secondary.dark" }, "Organization name"),
                        React.createElement(TextField, { required: true, fullWidth: true, variant: "outlined", onChange: handleChange, name: "organizationName", value: organizationName, placeholder: "Eg. John's company", slotProps: {
                                input: {
                                    sx: { borderRadius: '6px' },
                                },
                            } })),
                    React.createElement(Box, { mb: 3 },
                        React.createElement(Typography, { mb: 0.5, fontSize: "14px", fontWeight: 500, color: "secondary.dark" }, "Organization type"),
                        React.createElement(Select, { fullWidth: true, displayEmpty: true, variant: "outlined", value: organizationType, onChange: (e) => handleFormValues('organizationType', e.target.value), sx: {
                                fontSize: '0.875rem',
                                borderRadius: '6px',
                            } },
                            React.createElement(MenuItem, { value: "" }, "Select field"),
                            React.createElement(MenuItem, { value: "nonProfit" }, "Non-profit"),
                            React.createElement(MenuItem, { value: "r&d" }, "R&D Team"),
                            React.createElement(MenuItem, { value: "startup" }, "Startup"),
                            React.createElement(MenuItem, { value: "other" }, "Other"))),
                    React.createElement(Box, { mb: 3 },
                        React.createElement(Typography, { mb: 0.5, fontSize: "14px", fontWeight: 500, color: "secondary.dark" }, "Grant experience"),
                        React.createElement(Select, { fullWidth: true, displayEmpty: true, variant: "outlined", value: grantExperience, onChange: (e) => handleFormValues('grantExperience', e.target.value), sx: {
                                fontSize: '0.875rem',
                                borderRadius: '6px',
                            } },
                            React.createElement(MenuItem, { value: "" }, "Select field"),
                            React.createElement(MenuItem, { value: "noExperience" }, "No experience"),
                            React.createElement(MenuItem, { value: "someExperience" }, "Some experience"),
                            React.createElement(MenuItem, { value: "veryExperience" }, "Very experience"))),
                    React.createElement(FundingStrategyField, { handleFormValues: handleFormValues, currentValue: fundingStrategy }),
                    React.createElement(BandwidthField, { currentValue: bandwidth, handleFormValues: handleFormValues }),
                    React.createElement(Box, null,
                        React.createElement(Typography, { mb: 0.5, fontSize: "14px", fontWeight: 500, color: "secondary.dark" }, "Tell us what you\u2019d like to get funded."),
                        React.createElement(TextField, { required: true, fullWidth: true, variant: "outlined", onChange: handleChange, name: "organizationDescription", value: organizationDescription, multiline: true, minRows: 4, slotProps: {
                                input: {
                                    sx: {
                                        borderRadius: '6px',
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '14px',
                                        },
                                    },
                                },
                            }, placeholder: "Provide some content on the project or organization here.Feel free to copy and paste from prior applications, an About us page, or a company summary." }))),
                React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center" },
                    React.createElement(DefaultOutlinedButton, { title: "Go back", onClick: () => dispatch(FormsActions.setValue({ id: 'steps', step: 0 })) }),
                    React.createElement(DefaultContainedButton, { type: "submit", title: "Continue", loading: loading, loadingPosition: "center", sx: { width: '140px' } }))))));
};
